/* eslint-disable @typescript-eslint/ban-ts-comment */
"use client"

import React, { useState, useMemo } from "react"
import {
  CommandDialog,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command"
import { Button } from "@/components/ui/button"
import { Search } from "lucide-react"

import type { LawData, LawContent } from "@/actions/eGov"

/**
 * メイン画面にテキストボックス + 検索ボタンを設置。
 * → ボタンを押すとダイアログを開き、検索結果を表示。
 */
export default function KenchikuCodeSearchClient({ data }: { data: LawData }) {
  const [open, setOpen] = useState(false)

  // メイン画面のテキストボックスで入力中の文字列
  const [candidate, setCandidate] = useState("")
  // 検索確定後の文字列 (ダイアログで表示する検索結果用)
  const [searchText, setSearchText] = useState("")

  // 検索ロジック
  const matched = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    if (!q) return []
    const results: LawContent[] = []
    collectMatched(data.contents, q, results)
    return results
  }, [searchText, data.contents])

  // 「検索」ボタンを押した時:
  //  1) searchTextをcandidateに更新
  //  2) ダイアログを表示
  const handleSearch = () => {
    setSearchText(candidate)
    setOpen(true)
  }

  return (
    <div className="space-y-4">
      {/* メイン画面の検索UI */}
      <div className="flex gap-2">
        <input
          className="border rounded px-2 py-1 text-sm flex-1"
          placeholder="例:建築物など"
          value={candidate}
          onChange={(e) => setCandidate(e.target.value)}
        />
        <Button variant="outline" onClick={handleSearch}>
          <Search className="w-4 h-4 mr-1" />
          検索
        </Button>
      </div>

      {/* 検索ボタン押下後に表示するダイアログ */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        {/* shadcn/ui の内蔵フィルタを無効化 */}
        {/* @ts-ignore */}
        <CommandList shouldFilter={false}>
          <CommandEmpty>該当なし</CommandEmpty>
          <CommandSeparator />
          <CommandGroup heading="検索結果">
            {matched.map((item) => (
              <SearchResultItem key={item.id} item={item} query={searchText} />
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  )
}

/** 再帰的にタイトル/段落を探し、検索語を含むものを results に追加 */
function collectMatched(
  contents: LawContent[],
  q: string,
  results: LawContent[],
) {
  for (const c of contents) {
    // タイトルとパラグラフを正規化
    const titleLow = c.title.replace(/\s+/g, "").toLowerCase()
    const parasLow = c.paragraphs.join("").replace(/\s+/g, "").toLowerCase()

    if (titleLow.includes(q) || parasLow.includes(q)) {
      results.push(c)
    }
    if (c.children?.length) {
      collectMatched(c.children, q, results)
    }
  }
}

/**
 * 検索結果アイテム (タイトル + 条文本文)
 * shadcn/ui の内部フィルタを無効化しているので、value="" とする
 */
function SearchResultItem({
  item,
  query,
}: {
  item: LawContent
  query: string
}) {
  const handleSelect = () => {
    console.log("selected:", item.id)
    // location.hash = item.id; など
  }

  return (
    // 内部フィルタに引っかからないよう value="" に設定
    <CommandItem value="" onSelect={handleSelect}>
      <div className="flex flex-col gap-1">
        <span className="font-bold text-sm">
          {highlightKeyword(item.title, query)}
        </span>
        {item.paragraphs.length > 0 && (
          <div className="text-xs text-muted-foreground">
            {item.paragraphs.map((p, idx) => (
              <p key={idx}>{highlightKeyword(p, query)}</p>
            ))}
          </div>
        )}
      </div>
    </CommandItem>
  )
}

/** text内の query と部分一致する箇所を <mark> で囲んで返す */
function highlightKeyword(text: string, query: string): JSX.Element[] {
  if (!query) {
    return [<span key="no-query">{text}</span>]
  }
  const regex = new RegExp(`(${query})`, "gi")
  const parts = text.split(regex)
  return parts.map((part, idx) => {
    if (part.toLowerCase() === query.toLowerCase()) {
      return (
        <mark key={idx} className="bg-yellow-300">
          {part}
        </mark>
      )
    } else {
      return <span key={idx}>{part}</span>
    }
  })
}
